import { EBlockType } from './constants';

export const camelCaseToTitleCase = (str: string): string => {
  if (!str) return str;
  // Special case: cta should remain as CTA
  if (str.toLowerCase() === 'cta') {
    return 'CTA';
  }
  if (str.toLowerCase() === 'contentspreview') {
    return 'Content Preview';
  }
  // Insert a space before capital letters and capitalize the first letter
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
};

export const mapBlockTypeToIcon: Record<EBlockType, string> = {
  image: 'fa-light fa-image',
  video: 'fa-light fa-video',
  cta: 'fa-light fa-arrow-pointer',
  html: 'fa-light fa-code',
  contentsPreview: 'fa-slab-press fa-regular fa-eye',
  dailyBonus: 'fa-light fa-gift',
  progressBar: 'fa-light fa-star'
};

// Self-closing tags
const selfClosingTags = new Set([
  'img',
  'br',
  'hr',
  'input',
  'meta',
  'link',
  'area',
  'base',
  'col',
  'embed',
  'source',
  'track',
  'wbr'
]);

// Inline elements that shouldn't break lines
const inlineTags = new Set([
  'a',
  'span',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'code',
  'small',
  'sub',
  'sup',
  'mark',
  'del',
  'ins'
]);

export const formatElement = (
  element: Element | DocumentFragment,
  indent = 0
): string => {
  const indentStr = '  '.repeat(indent);
  let result = '';

  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text && text.trim()) {
        // Preserve text content, trim only leading/trailing whitespace
        const trimmed = text.trim();
        result += `${indentStr}${trimmed}\n`;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tagName = el.tagName.toLowerCase();
      const attributes = Array.from(el.attributes)
        .map((attr) => `${attr.name}="${attr.value}"`)
        .join(' ');
      const attrStr = attributes ? ` ${attributes}` : '';

      const hasElementChildren = el.children.length > 0;
      const isSelfClosing = selfClosingTags.has(tagName);
      const isInline = inlineTags.has(tagName);

      if (isSelfClosing) {
        result += `${indentStr}<${tagName}${attrStr} />\n`;
      } else if (isInline && !hasElementChildren) {
        // Inline elements with only text content - keep on one line
        const innerText = Array.from(el.childNodes)
          .map((n) => {
            if (n.nodeType === Node.TEXT_NODE) {
              return n.textContent?.trim() || '';
            }
            return '';
          })
          .join('')
          .trim();
        if (innerText) {
          result += `${indentStr}<${tagName}${attrStr}>${innerText}</${tagName}>\n`;
        } else {
          result += `${indentStr}<${tagName}${attrStr}></${tagName}>\n`;
        }
      } else {
        // Block elements or elements with children
        result += `${indentStr}<${tagName}${attrStr}>\n`;
        result += formatElement(el, indent + 1);
        result += `${indentStr}</${tagName}>\n`;
      }
    }
  }

  return result;
};
